<script lang="ts">
	import type { FooterLinks, SEOData } from '$lib/components/seo/types';
	import Image from '$lib/components/atoms/image/Image.svelte';
	import Link from '$lib/components/atoms/link/Link.svelte';
	import { getLanguageCode } from '$lib/i18n/language';
	import FaqFooter from './FAQFooter.svelte';
	import FooterPagination from './FooterPagination.svelte';
	import FooterPartnership from './FooterPartnership.svelte';
	import { segments2href } from './linkSegments';
	import PlainSeoFooter from './PlainSEOFooter.svelte';
	import StructuredSeoFooter from './StructuredSEOFooter.svelte';

	const lang = getLanguageCode();

	interface Props {
		partnersLinks: FooterLinks[];
		seo: SEOData;
	}

	const { partnersLinks, seo }: Props = $props();
	const title
		= $derived((seo?.footer?.data.type === 'plain' && seo.footer.data.title)
			|| seo?.footer?.title
			|| seo?.header?.og?.title);
	const textAsHtml = $derived(seo?.footer?.data.type === 'plain' && !!seo.footer.data.text);
	const text
		= $derived((seo?.footer?.data.type === 'plain' && seo.footer.data.text)
			|| seo?.footer?.description
			|| seo?.header?.og?.description);
	const crumbs = $derived(seo?.footer?.crumbs);
	const pagination = $derived(seo?.footer?.pagination);
</script>

{#if crumbs && crumbs.length > 0}
	<ol
		itemscope
		itemtype="http://schema.org/BreadcrumbList"
		class="breadcrumbs flex flex-wrap p-0 items-center whitespace-nowrap"
	>
		<li itemprop="itemListElement" itemtype="http://schema.org/ListItem" itemscope>
			<Link itemprop="item" class="inline" href="/">
				<span itemprop="name">Home</span>
			</Link>
			<meta itemprop="position" content="0" />
		</li>
		<!-- eslint-disable-next-line svelte/require-each-key -->
		{#each crumbs as crumb, i}
			{#if crumb.label}
				<li itemprop="itemListElement" itemtype="http://schema.org/ListItem" itemscope>
					{#if i < crumbs.length - 1}
						<Link itemprop="item" class="inline" href={segments2href(crumbs.slice(0, i + 1), lang)} svelteKitPreloadData="off">
							<span itemprop="name">{crumb.label}</span>
						</Link>
					{:else}
						<span itemprop="name" class="text-md text-neutral-strongest">{crumb.label}</span>
					{/if}
					<meta itemprop="position" content={`${i + 1}`} />
				</li>
			{/if}
		{/each}
	</ol>
{/if}

{#if title}
	<h1 class="text-md font-medium">{title}</h1>
{/if}
{#if text}
	{#if textAsHtml}
		<!-- eslint-disable-next-line svelte/no-at-html-tags -->
		<div class="description">{@html text}</div>
	{:else}
		<p class="description">{text}</p>
	{/if}
{/if}

{#if seo.footer?.staticMap}
	<div class="flex justify-center">
		<Image src={seo.footer.staticMap.url} height="auto" width="360px" alt={title || ''} />
	</div>
{/if}

{#if seo.footer?.data.type === 'structured'}
	<StructuredSeoFooter data={seo.footer.data} />
{:else if seo.footer?.data.type === 'plain'}
	<PlainSeoFooter data={seo.footer.data} />
{/if}

{#if pagination}
	<FooterPagination currentPage={pagination.currentPage} pageCount={pagination.pageCount} />
{/if}

{#if seo.footer?.faq}
	<FaqFooter data={seo.footer.faq} />
{/if}

<FooterPartnership {partnersLinks} />

<style>
	ol {
		list-style: none;
		margin: 0;
	}
	.breadcrumbs li + li:before {
		padding: 0 8px;
		content: ' > ';
	}
</style>
